import { type PointerEvent, useRef } from 'react'

// A finger that travelled further than this was panning, not tapping.
const TAP_SLOP_PX = 8

/**
 * Pointer handlers that call onTap for a one-finger tap. Read from pointer
 * events rather than click: the zoom library cancels touchstart, which stops
 * the browser from synthesising clicks.
 */
export function useTap(onTap: (e: PointerEvent) => void) {
  const pointersRef = useRef(new Set<number>())
  const gestureRef = useRef<{ x: number; y: number; multi: boolean } | null>(
    null,
  )

  const onPointerDown = (e: PointerEvent) => {
    pointersRef.current.add(e.pointerId)
    if (pointersRef.current.size === 1) {
      gestureRef.current = { x: e.clientX, y: e.clientY, multi: false }
    } else if (gestureRef.current) {
      // A second finger makes it a pinch, never a tap.
      gestureRef.current.multi = true
    }
  }

  const onPointerUp = (e: PointerEvent) => {
    pointersRef.current.delete(e.pointerId)
    const gesture = gestureRef.current
    if (pointersRef.current.size) return
    gestureRef.current = null
    if (!gesture || gesture.multi) return
    if (Math.hypot(e.clientX - gesture.x, e.clientY - gesture.y) > TAP_SLOP_PX)
      return
    onTap(e)
  }

  const onPointerCancel = (e: PointerEvent) => {
    pointersRef.current.delete(e.pointerId)
    gestureRef.current = null
  }

  return { onPointerDown, onPointerUp, onPointerCancel }
}
